import type { PublicClientApplication } from '@azure/msal-browser';
import type { AccountInfo } from '@azure/msal-common';
import { isRef, type ComputedRef, type MaybeRef } from 'vue';

export type AccountSelectorInput =
    | ((
          accounts: AccountInfo[],
          msalInstance: PublicClientApplication
      ) => AccountInfo | null | undefined)
    | number
    | null
    | undefined;

export type AccountSelector = (
    accounts: AccountInfo[],
    msalInstance: PublicClientApplication
) => AccountInfo | null;

export type MaybeAccountRef =
    | MaybeRef<AccountSelectorInput | AccountInfo>
    | ComputedRef<AccountSelectorInput | AccountInfo>;

/**
 * Normalizes an account selector.
 *
 * @param selector The account selector input.
 */
export function normalizeAccountSelector(
    selector?: AccountSelectorInput
): AccountSelector {
    if (!selector) return (accounts) => accounts[0] ?? null;

    if (typeof selector === 'number')
        return (accounts, msalInstance) =>
            accounts[selector] ?? msalInstance.getActiveAccount();

    return (accounts, msalInstance) =>
        selector(accounts, msalInstance) ?? msalInstance.getActiveAccount();
}

/**
 * Returns a function that executes the provided selector each time it is called.
 *
 * @param selector The account selector input.
 * @param msalInstance The msal instance form which the accounts are selected.
 */
export function createAccountGetter(
    msalInstance: PublicClientApplication,
    selector?: AccountSelectorInput
): () => AccountInfo | null {
    const normalizedSelector = normalizeAccountSelector(selector);
    return () =>
        normalizedSelector(msalInstance.getAllAccounts(), msalInstance);
}

/**
 * Executes an account selector on an msal browser instance.
 *
 * @param selector The account selector.
 * @param msalInstance The msal instance.
 */
export function getAccount(
    msalInstance: PublicClientApplication,
    selector?: AccountSelectorInput
): AccountInfo | null {
    return createAccountGetter(msalInstance, selector)();
}

export function resolveAccountUnref(
    msalInstance: PublicClientApplication | null,
    maybeRef?: MaybeAccountRef
): AccountInfo | null {
    const input = isRef(maybeRef) ? maybeRef.value : maybeRef;
    if (input && typeof input === 'object') return input as AccountInfo;
    if (!msalInstance) return null;
    return normalizeAccountSelector(input as AccountSelectorInput)(
        msalInstance.getAllAccounts(),
        msalInstance
    );
}
